import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiPlus } from "react-icons/fi";

import useBroadcastList from "./useBroadcastList";
import { BroadcastHeadline } from "./BroadcastHeadline";
import { BroadcastFilters } from "./BroadcastFilters";
import { BroadcastRowGrid } from "./BroadcastRowGrid";
import { BroadcastEmpty } from "./BroadcastEmpty";
import { Tabs } from "./ui/Tabs";
import { CHANNEL_TABS } from "./broadcastEnums";
import { SkeletonRows } from "../../../common/comms/SkeletonRows";
import { Toaster } from "../../../common/Toaster/Toaster";

/**
 * Copy under the page title, per channel tab. Each says something true about
 * who that tab can reach, because "14 broadcasts" means three different things
 * depending on which tab you are looking at.
 */
const CHANNEL_NOTES = {
  all: "Push and email are counted separately — a user reached on both is one unique user here.",
  push: "Push reaches app users with a live device token.",
  email: "Email reaches opted-in contacts, including people who never installed the app.",
};

/**
 * The Broadcasts hub.
 *
 * Composition only: every fetch, filter and race lives in `useBroadcastList`,
 * every number is formatted by `Num`, and every empty state is chosen here and
 * rendered by `BroadcastEmpty`. This file should stay boring.
 */
export default function BroadcastList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [toast, setToast] = useState(null);

  const {
    channel,
    status,
    days,
    setChannel,
    setStatus,
    setDays,
    rows,
    overview,
    loading,
    error,
    forbidden,
    hasFilters,
    clearFilters,
    refresh,
  } = useBroadcastList();

  // Success message handed over by the composer on navigate. Kept verbatim
  // from the previous page: the composer navigates away immediately after a
  // send, so this is the only place the confirmation can surface.
  useEffect(() => {
    if (location.state?.flash) {
      setToast({ message: location.state.flash, type: "success" });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  /**
   * A 403 replaces the page rather than decorating it. The spec is explicit:
   * "Do not render the shell behind an error." Showing tabs and an empty
   * table to someone who is not allowed to see broadcasts implies there is
   * nothing to see, which is a different and wrong statement.
   */
  if (forbidden) {
    return (
      <main className="min-h-screen bg-bc-page px-10 py-9">
        <BroadcastEmpty variant="forbidden" />
      </main>
    );
  }

  const showEmpty = !loading && !error && rows.length === 0;

  return (
    <main className="min-h-screen bg-bc-page px-10 py-9">
      <div className="mx-auto max-w-[1120px]">
        <header className="mb-5 flex items-start justify-between gap-5">
          <div>
            <h1 className="text-[26px] font-semibold tracking-[-0.02em] text-bc-text">
              Broadcasts
            </h1>
            <p className="mt-1 text-xs text-bc-soft">
              One title, one description, one image, one destination. Sent as a
              push, an email, or both.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/broadcasts/new")}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-[7px] bg-orange-primary px-4 py-2 text-[13px] font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-2"
          >
            <FiPlus aria-hidden="true" />
            New broadcast
          </button>
        </header>

        <Tabs
          value={channel}
          onChange={setChannel}
          items={CHANNEL_TABS}
          label="Channel"
        />
        <p className="mb-4 mt-3 text-xs text-bc-soft">{CHANNEL_NOTES[channel]}</p>

        <BroadcastHeadline
          overview={overview}
          channel={channel}
          loading={loading}
          days={days}
          className="mb-4"
        />

        <BroadcastFilters
          status={status}
          onStatusChange={setStatus}
          days={days}
          onDaysChange={setDays}
          className="mb-3.5"
        />

        {loading ? (
          <SkeletonRows variant="rows" count={6} />
        ) : error ? (
          <BroadcastEmpty variant="error" message={error} onRetry={refresh} />
        ) : showEmpty ? (
          // Two different facts, two different states. A first run means
          // "make one"; a filtered miss means "widen the net". Telling a new
          // user they have a filter problem is a small lie that costs them
          // several minutes.
          <BroadcastEmpty
            variant={hasFilters ? "filtered" : "first-run"}
            onClear={clearFilters}
            onCreate={() => navigate("/broadcasts/new")}
          />
        ) : (
          <BroadcastRowGrid
            rows={rows}
            onOpen={(id) => navigate(`/broadcasts/${id}/analytics`)}
          />
        )}
      </div>

      {/* Toaster wraps react-toastify and self-dismisses; it takes no close
          handler. Keying on the message remounts it so two consecutive sends
          each raise their own toast instead of the second being swallowed. */}
      {toast ? (
        <Toaster key={toast.message} message={toast.message} type={toast.type} />
      ) : null}
    </main>
  );
}
